import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { cleanSubmissionAnswers } from '@/utils/cleanAnswer';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { AlertCircle, CheckCircle, Clock, FileText, MessageSquare, X, Download } from 'lucide-react';
import axios from 'axios';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

const ResultViewPage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [activeTask, setActiveTask] = useState(1);
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  const [pdfDownloadEnabled, setPdfDownloadEnabled] = useState(false);
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [signatureStyle, setSignatureStyle] = useState('name'); // 'name' or 'code'

  useEffect(() => {
    fetchResult();
  }, [token]);

  const fetchResult = async () => {
    try {
      const response = await axios.get(`/result/${token}`);
      setData({ ...response.data, submission: cleanSubmissionAnswers(response.data.submission) });
      
      // Fetch PDF download setting and signature style from PUBLIC endpoint
      try {
        const settingsResponse = await axios.get('/public/settings/result-page');
        setPdfDownloadEnabled(settingsResponse.data?.pdf_download_enabled || false);
        setSignatureStyle(settingsResponse.data?.signature_style || 'name');
      } catch (error) {
        console.log('Could not fetch settings, using defaults');
      }
      
      // Set active task based on configuration
      if (response.data.test_details?.task_configuration === 'task_2_only') {
        setActiveTask(2);
      } else {
        setActiveTask(1);
      }
    } catch (err) {
      if (err.response?.status === 410) {
        setError({ type: 'expired', message: err.response.data.detail });
      } else if (err.response?.status === 404) {
        setError({ type: 'not_found', message: err.response.data.detail });
      } else {
        setError({ type: 'error', message: 'Failed to load result' });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPDF = async () => {
    setGeneratingPdf(true);
    try {
      const element = document.getElementById('feedback-content');
      if (!element) return;

      // Create canvas from the element
      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff'
      });

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = canvas.width;
      const imgHeight = canvas.height;
      const ratio = Math.min(pdfWidth / imgWidth, pdfHeight / imgHeight);
      const imgX = (pdfWidth - imgWidth * ratio) / 2;
      const imgY = 0;

      // Add image to PDF
      pdf.addImage(imgData, 'PNG', imgX, imgY, imgWidth * ratio, imgHeight * ratio);

      // Check if content exceeds one page
      if (imgHeight * ratio > pdfHeight) {
        let heightLeft = imgHeight * ratio - pdfHeight;
        let position = -pdfHeight;

        while (heightLeft > 0) {
          pdf.addPage();
          pdf.addImage(imgData, 'PNG', imgX, position, imgWidth * ratio, imgHeight * ratio);
          heightLeft -= pdfHeight;
          position -= pdfHeight;
        }
      }

      // Download PDF
      const candidateName = data.candidate_name || 'Student';
      const testTitle = data.test_details?.title || 'Test';
      pdf.save(`${candidateName}_${testTitle}_Feedback.pdf`);
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Failed to generate PDF. Please try again.');
    } finally {
      setGeneratingPdf(false);
    }
  };

  const renderHighlightedAnswer = (answer, task) => {
    if (!answer || !data.comments) return answer;

    const taskComments = data.comments.filter(c => c.task_num === task);
    if (taskComments.length === 0) return answer;

    // Remove duplicate comments (same start and end position)
    const uniqueComments = [];
    const seenPositions = new Set();
    taskComments.forEach(comment => {
      const posKey = `${comment.highlight_start}-${comment.highlight_end}`;
      if (!seenPositions.has(posKey)) {
        seenPositions.add(posKey);
        uniqueComments.push(comment);
      }
    });

    // Create a map of character positions to their covering highlights
    const charHighlights = new Array(answer.length).fill(null).map(() => []);
    
    uniqueComments.forEach(comment => {
      const start = Math.max(0, comment.highlight_start);
      const end = Math.min(answer.length, comment.highlight_end);
      for (let i = start; i < end; i++) {
        charHighlights[i].push(comment);
      }
    });
    
    // Build result by grouping consecutive characters with same highlight combination
    let result = '';
    let i = 0;
    let commentIdx = 0;
    
    while (i < answer.length) {
      const currentHighlights = charHighlights[i];
      
      if (currentHighlights.length === 0) {
        // No highlight - find the end of unhighlighted text
        let j = i;
        while (j < answer.length && charHighlights[j].length === 0) {
          j++;
        }
        result += answer.substring(i, j);
        i = j;
      } else {
        // Has highlights - find end of this highlight group
        const currentIds = currentHighlights.map(h => `${h.highlight_start}-${h.highlight_end}`).sort().join(',');
        let j = i;
        while (j < answer.length) {
          const nextHighlights = charHighlights[j];
          const nextIds = nextHighlights.map(h => `${h.highlight_start}-${h.highlight_end}`).sort().join(',');
          if (nextIds !== currentIds) break;
          j++;
        }
        
        const text = answer.substring(i, j);
        
        // Check for both word-level and line-level highlights
        let hasWordLevel = false;
        let hasLineLevel = false;
        let wordLevelComment = null;
        let lineLevelComment = null;
        
        currentHighlights.forEach(h => {
          const hText = answer.substring(h.highlight_start, h.highlight_end);
          const hWordCount = hText.trim().split(/\s+/).filter(w => w.length > 0).length;
          if (hWordCount <= 3) {
            hasWordLevel = true;
            wordLevelComment = h;
          } else {
            hasLineLevel = true;
            lineLevelComment = h;
          }
        });
        
        let style, className;
        const primaryComment = wordLevelComment || lineLevelComment;
        
        if (hasWordLevel && hasLineLevel) {
          // BOTH: Yellow background + Circle + red underline
          style = `
            background-color: #fef9c3;
            border: 1.5px solid #dc2626;
            border-radius: 50%;
            padding: 0px 3px;
            box-shadow: 0 2px 0 0 #dc2626;
            cursor: pointer;
            position: relative;
            display: inline;
          `.replace(/\s+/g, ' ').trim();
          className = 'inline-feedback word-level-highlight has-parent';
        } else if (hasWordLevel) {
          // Word-level only: Circle + red underline
          style = `
            border: 1.5px solid #dc2626;
            border-radius: 50%;
            padding: 0px 3px;
            box-shadow: 0 2px 0 0 #dc2626;
            cursor: pointer;
            position: relative;
            display: inline;
          `.replace(/\s+/g, ' ').trim();
          className = 'inline-feedback word-level-highlight';
        } else {
          // Line-level only: Yellow background + red underline
          style = `
            background-color: #fef9c3;
            border-bottom: 2px solid #dc2626;
            cursor: pointer;
            position: relative;
          `.replace(/\s+/g, ' ').trim();
          className = 'inline-feedback line-level-highlight';
        }
        
        // Determine highlight type for data attribute
        const highlightType = (hasWordLevel && hasLineLevel) ? 'both' : (hasWordLevel ? 'word' : 'line');
        
        // Get the original text based on primaryComment (which is wordLevel for nested, or the only one available)
        const originalText = answer.substring(primaryComment.highlight_start, primaryComment.highlight_end);
        
        // Also store line-level text separately if it exists (for showing parent context)
        const lineText = lineLevelComment ? answer.substring(lineLevelComment.highlight_start, lineLevelComment.highlight_end) : '';
        
        const commentId = `feedback-${task}-${commentIdx++}`;
        const highlightHtml = `<span class="${className}" data-comment-id="${commentId}" data-highlight-type="${highlightType}" data-feedback="${encodeURIComponent(primaryComment.comment_text)}" data-original-text="${encodeURIComponent(originalText)}" data-line-text="${encodeURIComponent(lineText)}" style="${style}">${text}</span>`;
        
        result += highlightHtml;
        i = j;
      }
    }

    return result;
  };

  const handleAnswerClick = (e) => {
    const target = e.target;
    if (target.classList.contains('inline-feedback')) {
      const feedbackText = decodeURIComponent(target.getAttribute('data-feedback'));
      // Use data-original-text if available (for nested highlights), otherwise use textContent
      const originalText = target.getAttribute('data-original-text');
      const highlightedText = originalText ? decodeURIComponent(originalText) : target.textContent;
      setSelectedFeedback({ text: feedbackText, highlighted: highlightedText });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-pink-50 flex items-center justify-center p-4">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-slate-600">Loading your results...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-pink-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardHeader>
            <div className="flex items-center space-x-2">
              <AlertCircle className={`w-6 h-6 ${error.type === 'expired' ? 'text-orange-500' : 'text-red-500'}`} />
              <CardTitle className="text-lg">
                {error.type === 'expired' ? 'Link Expired' : 'Result Not Found'}
              </CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-slate-600 mb-4">{error.message}</p>
            {error.type === 'expired' && (
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                <p className="text-sm text-orange-800">
                  This feedback link was valid for 5 days and has now expired. Please contact your instructor if you need access to your results.
                </p>
              </div>
            )}
            <Button
              onClick={() => navigate('/')}
              className="w-full mt-4 hover:opacity-90"
              style={{background: 'linear-gradient(135deg, #2D757A 0%, #3A8F97 100%)'}}
            >
              Go to Home
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { submission, marks, test_details, token_expires_at } = data;
  const task1Band = marks.task1_scores.band_score;
  const task2Band = marks.task2_scores.band_score;
  const overallBand = marks.final_band;
  const expiresAt = new Date(token_expires_at);
  const daysLeft = Math.ceil((expiresAt - new Date()) / (1000 * 60 * 60 * 24));

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 to-cyan-50">
      {/* Header */}
      <div className="header-gradient">
        <div className="max-w-6xl mx-auto px-4 py-6">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-white">Your IELTS Writing Result</h1>
              <p className="text-white opacity-90 text-sm sm:text-base mt-1">
                {submission.candidate_name} • {test_details?.title || 'Writing Test'}
              </p>
              {submission.submitted_at && (
                <p className="text-white opacity-75 text-xs sm:text-sm mt-1">
                  Test Date: {new Date(submission.submitted_at).toLocaleDateString('en-GB', { 
                    day: '2-digit', 
                    month: 'short', 
                    year: 'numeric' 
                  })}
                </p>
              )}
            </div>
            <div className="flex flex-col gap-2 items-end">
              {pdfDownloadEnabled && (
                <Button
                  onClick={handleDownloadPDF}
                  disabled={generatingPdf}
                  className="bg-white text-teal-700 hover:bg-teal-50 font-semibold shadow-lg"
                >
                  <Download className="w-4 h-4 mr-2" />
                  {generatingPdf ? 'Generating PDF...' : 'Download PDF'}
                </Button>
              )}
              {daysLeft > 0 && (
                <div className="bg-white bg-opacity-20 backdrop-blur-sm rounded-lg px-4 py-2 flex items-center gap-2">
                  <Clock className="w-4 h-4 text-white" />
                  <span className="text-white text-sm font-medium">
                    Link expires in {daysLeft} day{daysLeft !== 1 ? 's' : ''}
                  </span>
                </div>
              )}
              <p className="text-white opacity-75 text-xs">
                Valid for 7 days from publish
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div id="feedback-content" className="max-w-6xl mx-auto px-4 py-8">
        {/* Overall Band Score */}
        <Card className="mb-6 border-none" style={{background: 'linear-gradient(135deg, #1D5259 0%, #3A8F97 100%)'}}>
          <CardContent className="py-8 text-center">
            <p className="text-white text-sm sm:text-base opacity-90 mb-2">Your Overall Band Score</p>
            <p className="text-white text-6xl sm:text-7xl font-bold">{overallBand}</p>
            {(test_details?.task_configuration === 'full' || !test_details?.task_configuration) && (
              <p className="text-white text-xs sm:text-sm mt-4 opacity-80">
                Task 1: {task1Band} • Task 2: {task2Band}
              </p>
            )}
            {test_details?.task_configuration === 'task_1_only' && (
              <p className="text-white text-xs sm:text-sm mt-4 opacity-80">
                Task 1 Only
              </p>
            )}
            {test_details?.task_configuration === 'task_2_only' && (
              <p className="text-white text-xs sm:text-sm mt-4 opacity-80">
                Task 2 Only
              </p>
            )}
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left: Answer with highlights */}
          <div className="lg:col-span-2 space-y-4">
            {/* Task Tabs - Only show for full test */}
            {(test_details?.task_configuration === 'full' || !test_details?.task_configuration) && (
              <div className="flex gap-2 bg-white rounded-lg p-2 shadow">
                <button
                  className={`flex-1 py-2 px-4 rounded-md transition-all text-sm sm:text-base ${
                    activeTask === 1
                      ? 'text-white'
                      : 'text-slate-600 hover:bg-slate-100'
                  }`}
                  style={activeTask === 1 ? {background: 'linear-gradient(135deg, #E53935 0%, #B71C1C 100%)'} : {}}
                  onClick={() => setActiveTask(1)}
                >
                  Task 1
                </button>
                <button
                  className={`flex-1 py-2 px-4 rounded-md transition-all text-sm sm:text-base ${
                    activeTask === 2
                      ? 'text-white'
                      : 'text-slate-600 hover:bg-slate-100'
                  }`}
                  style={activeTask === 2 ? {background: 'linear-gradient(135deg, #E53935 0%, #B71C1C 100%)'} : {}}
                  onClick={() => setActiveTask(2)}
                >
                  Task 2
                </button>
              </div>
            )}

            {/* Answer */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base sm:text-lg">Your Answer - Task {activeTask}</CardTitle>
              </CardHeader>
              <CardContent>
                <div
                  className="text-slate-700 leading-relaxed whitespace-pre-wrap text-sm sm:text-base"
                  onClick={handleAnswerClick}
                  dangerouslySetInnerHTML={{
                    __html: renderHighlightedAnswer(
                      activeTask === 1 ? submission.task1_answer : submission.task2_answer,
                      activeTask
                    )
                  }}
                />
              </CardContent>
            </Card>

          </div>

          {/* Right: Scores & Feedback */}
          <div className="space-y-4">
            {/* Task Scores */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base sm:text-lg">Task {activeTask} Scores</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {(() => {
                  const scores = activeTask === 1 ? marks.task1_scores : marks.task2_scores;
                  const criteriaNames = {
                    task_achievement: activeTask === 1 ? 'Task Achievement' : 'Task Response',
                    coherence_cohesion: 'Coherence & Cohesion',
                    lexical_resource: 'Lexical Resource',
                    grammar_accuracy: 'Grammatical Range & Accuracy'
                  };

                  return Object.entries(criteriaNames).map(([key, label]) => (
                    <div key={key} className="flex justify-between items-center">
                      <span className="text-xs sm:text-sm text-slate-600">{label}</span>
                      <span className="font-bold text-sm sm:text-base" style={{background: 'linear-gradient(135deg, #2E7D32 0%, #00C853 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text'}}>{scores[key]}</span>
                    </div>
                  ));
                })()}
                <div className="pt-3 border-t">
                  <div className="flex justify-between items-center">
                    <span className="font-semibold text-slate-700 text-sm sm:text-base">Band Score</span>
                    <span className="text-2xl sm:text-3xl font-bold" style={{background: 'linear-gradient(135deg, #2E7D32 0%, #00C853 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text'}}>
                      {activeTask === 1 ? task1Band : task2Band}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Task-specific Summary */}
            {(marks.instructor_summary_task1 || marks.instructor_summary_task2 || marks.instructor_summary) && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-base sm:text-lg flex items-center gap-2">
                    <FileText className="w-4 h-4" />
                    Instructor's Summary
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Task 1 Summary */}
                  {marks.instructor_summary_task1 && (
                    <div className="border-l-4 border-blue-500 pl-4">
                      <h4 className="text-sm font-semibold text-blue-700 mb-2">Task 1</h4>
                      <p className="text-slate-700 text-sm leading-relaxed whitespace-pre-wrap">{marks.instructor_summary_task1}</p>
                    </div>
                  )}
                  {/* Task 2 Summary */}
                  {marks.instructor_summary_task2 && (
                    <div className="border-l-4 border-purple-500 pl-4">
                      <h4 className="text-sm font-semibold text-purple-700 mb-2">Task 2</h4>
                      <p className="text-slate-700 text-sm leading-relaxed whitespace-pre-wrap">{marks.instructor_summary_task2}</p>
                    </div>
                  )}
                  {/* Legacy Summary (backward compatibility) */}
                  {!marks.instructor_summary_task1 && !marks.instructor_summary_task2 && marks.instructor_summary && (
                    <p className="text-slate-700 text-sm leading-relaxed whitespace-pre-wrap">{marks.instructor_summary}</p>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Task-specific Voice Notes */}
            {(marks.voice_note_task1 || marks.voice_note_task2 || marks.voice_note) && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-base sm:text-lg flex items-center gap-2">
                    <MessageSquare className="w-4 h-4" />
                    Voice Feedback
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Task 1 Voice Note */}
                  {marks.voice_note_task1 && (
                    <div className="border-l-4 border-blue-500 pl-4">
                      <h4 className="text-sm font-semibold text-blue-700 mb-2">Task 1</h4>
                      <audio 
                        controls 
                        controlsList="nodownload noplaybackrate"
                        className="w-full" 
                        src={marks.voice_note_task1}
                        onContextMenu={(e) => e.preventDefault()}
                      >
                        Your browser does not support audio playback.
                      </audio>
                    </div>
                  )}
                  {/* Task 2 Voice Note */}
                  {marks.voice_note_task2 && (
                    <div className="border-l-4 border-purple-500 pl-4">
                      <h4 className="text-sm font-semibold text-purple-700 mb-2">Task 2</h4>
                      <audio 
                        controls 
                        controlsList="nodownload noplaybackrate"
                        className="w-full" 
                        src={marks.voice_note_task2}
                        onContextMenu={(e) => e.preventDefault()}
                      >
                        Your browser does not support audio playback.
                      </audio>
                    </div>
                  )}
                  {/* Legacy Voice Note (backward compatibility) */}
                  {!marks.voice_note_task1 && !marks.voice_note_task2 && marks.voice_note && (
                    <audio 
                      controls 
                      controlsList="nodownload noplaybackrate"
                      className="w-full" 
                      src={marks.voice_note}
                      onContextMenu={(e) => e.preventDefault()}
                    >
                      Your browser does not support audio playback.
                    </audio>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Instructor Signature - Dynamic based on setting */}
            {(data.instructor_name || data.instructor_code || data.instructor_designation) && (
              <div className="mt-6 text-center pb-4">
                <p className="text-sm text-slate-600 mb-2">Marked by:</p>
                <div className="inline-block">
                  {signatureStyle === 'code' ? (
                    // Code Style: Shows instructor code with line separator
                    <>
                      {data.instructor_code && (
                        <p className="text-base font-semibold text-slate-700 mb-1" style={{fontFamily: "'Montserrat', sans-serif"}}>
                          Instructor Code: {data.instructor_code}
                        </p>
                      )}
                      <div className="border-b-2 border-slate-300 mb-2 mx-8"></div>
                      <p className="text-sm text-slate-500" style={{fontFamily: "'Montserrat', sans-serif", fontWeight: 300}}>
                        {data.instructor_designation || 'IELTS Instructor'} — Radiance Education
                      </p>
                    </>
                  ) : (
                    // Name Style: Shows instructor name with signature font
                    <>
                      <div className="border-b-2 border-slate-300 pb-2 px-8">
                        <p className="text-3xl italic" style={{fontFamily: "'Great Vibes', cursive", color: 'rgb(22, 113, 127)'}}>
                          {data.instructor_name || 'Authority'}
                        </p>
                      </div>
                      {data.instructor_designation && (
                        <p className="text-sm text-slate-500 mt-2" style={{fontFamily: "'Montserrat', sans-serif", fontWeight: 300}}>
                          ({data.instructor_designation} - Radiance Education)
                        </p>
                      )}
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Feedback Modal */}
      {selectedFeedback && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
          onClick={() => setSelectedFeedback(null)}
        >
          <div 
            className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 animate-in fade-in zoom-in duration-200"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between mb-4">
              <h3 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
                <MessageSquare className="w-5 h-5" style={{color: '#3A8F97'}} />
                Inline Feedback
              </h3>
              <button
                onClick={() => setSelectedFeedback(null)}
                className="text-slate-400 hover:text-slate-600 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <p className="text-xs text-slate-500 mb-2">Selected Text:</p>
                <div className="bg-yellow-100 border border-yellow-300 rounded-md p-3">
                  <p className="text-sm font-medium text-slate-800">"{selectedFeedback.highlighted}"</p>
                </div>
              </div>
              
              <div>
                <p className="text-xs text-slate-500 mb-2">Instructor's Comment:</p>
                <div className="bg-slate-50 border border-slate-200 rounded-md p-3">
                  <p className="text-sm text-slate-700 leading-relaxed">{selectedFeedback.text}</p>
                </div>
              </div>
            </div>
            
            <div className="mt-6">
              <Button
                onClick={() => setSelectedFeedback(null)}
                className="w-full hover:opacity-90"
                style={{background: 'linear-gradient(135deg, #E53935 0%, #B71C1C 100%)'}}
              >
                Close
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ResultViewPage;
